import {
  CheckboxGroupInput,
  Create,
  Datagrid,
  DeleteWithConfirmButton,
  Edit,
  FunctionField,
  List,
  ListContextProvider,
  Pagination,
  SimpleForm,
  TextInput,
  useListContext,
  Validator,
  useNotify,
  useRedirect,
  Toolbar,
  SaveButton,
  BulkDeleteWithConfirmButton,
  useRecordContext,
  useInput,
  TopToolbar,
  TextField,
} from "react-admin";
import { useCustomPermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/PermissionGuard';
import {
  Select,
  MenuItem,
  Box,
  Chip,
  Checkbox,
  ListItemText,
  Card,
  CardHeader,
  CardContent,
  TextField as MuiTextField,
  Typography,
  Button,
  Autocomplete,
  Grid,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import "./styles.css";
import { useFormContext, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EditIcon from "@mui/icons-material/Edit";
import {
  Params,
  redirect,
  Link as RouterLink,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Add } from "@mui/icons-material";
import NoDataEmpty from "../NoDataEmpty";
import { customerDataApi } from "../../providers/env";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import MultiTabURLImportModal from "../../components/MultiTabURLImportModal";
import { Link as LinkIcon } from "@mui/icons-material";

const ActionsFunctionField = ({ agentId }: { agentId?: string }) => {
  const { hasPermission } = useCustomPermissions();

  return (
    <FunctionField
      label="Actions"
      render={(record: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGuard resource="customer_data" action="edit">
            <Button
              component={RouterLink}
              to={`/customer-data/${agentId}/${record.id}`}
              variant="outlined"
              size="small"
            >
              <EditIcon style={{ fontSize: 16, marginRight: 8 }} /> Edit
            </Button>
          </PermissionGuard>

          <PermissionGuard resource="customer_data" action="delete">
            <DeleteWithConfirmButton
              record={record}
              confirmTitle="Are you sure?"
              confirmContent="You won't be able to revert this!"
              mutationMode="pessimistic"
              redirect={`/customer-data/${agentId}`}
            />
          </PermissionGuard>
        </Box>
      )}
    />
  );
};

const requiredTrimmed =
  (message = "Required"): Validator =>
  (value: string | null | undefined) =>
    value?.trim() ? undefined : message;

const transformEdit = (data: any) => {
  const buildAttributesObject = (attrs: any) => {
    if (!attrs) return {};

    if (!Array.isArray(attrs) && typeof attrs === "object") {
      return Object.entries(attrs).reduce((acc: any, [k, v]) => {
        const key = String(k).replace(/[{}]/g, "").trim();
        const value = v == null ? "" : String(v).trim();
        if (key && value !== "") acc[key] = value;
        return acc;
      }, {});
    }

    return (attrs as any[])
      .filter((a) => a && String(a.key).trim() && String(a.value ?? "").trim())
      .reduce((acc: any, cur: any) => {
        const rawKey = String(cur.key ?? "")
          .replace(/[{}]/g, "")
          .trim();
        const rawVal = String(cur.value ?? "").trim();
        if (rawKey && rawVal !== "") acc[rawKey] = rawVal;
        return acc;
      }, {});
  };

  const attrsObj = buildAttributesObject(data.attributes);

  const base = {
    ...data,
    key: data.key?.trim(),
    value: data.value?.trim(),
  };

  if (attrsObj && Object.keys(attrsObj).length > 0) {
    return {
      ...base,
      attributes: attrsObj,
    };
  }

  return base;
};

// URL Import Button Component
interface URLImportButtonProps {
  onExtracted: (data: { key: string; value: string; tags: string[] }) => void;
}

const URLImportButton: React.FC<URLImportButtonProps> = ({ onExtracted }) => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <PermissionGuard resource="customer_data" action="create">
      <Button
        variant="outlined"
        startIcon={<LinkIcon />}
        onClick={() => setModalOpen(true)}
        sx={{
          margin: 2,
          borderStyle: "dashed",
          "&:hover": {
            borderStyle: "solid",
          },
        }}
      >
        Import from URL
      </Button>

      <MultiTabURLImportModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </PermissionGuard>
  );
};

const ListActions: React.FC<{ agentId?: string }> = ({ agentId }) => {
  const navigate = useNavigate();

  return (
    <TopToolbar>
      <PermissionGuard resource="customer_data" action="create">
        <Button
          variant="contained"
          onClick={() => navigate(`/customer-data/${agentId ?? ""}/create`)}
        >
          <Add fontSize="small" sx={{ mr: 0.5 }} /> Create
        </Button>
      </PermissionGuard>
    </TopToolbar>
  );
};

export const DynamicContextList = (props: any) => {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  
  const VariablePagination = (props: any) => (
    <Pagination rowsPerPageOptions={[25, 50, 100]} {...props} />
  );
  
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  const handleURLExtracted = (extractedData: { key: string; value: string; tags: string[] }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('key', extractedData.key);
    searchParams.set('value', extractedData.value);
    searchParams.set('tags', JSON.stringify(extractedData.tags));
    
    redirect(`/customer-data/create?${searchParams.toString()}`);
  };

  return (
    <List
      {...props}
      filters={[]}
      title={false}
      component="div"
      perPage={25}
      pagination={<VariablePagination />}
      actions={<ListActions agentId={agentId} />}
      empty={<NoDataEmpty entityKey="customer-data" agentId={agentId} />}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mr: 2 }}>
        <URLImportButton onExtracted={handleURLExtracted} />
      </Box>
      
      <TagFilterWrapper
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        agentId={agentId}
      />
    </List>
  );
};

const TagChips = ({ record }: { record?: any }) => {
  if (!record || !record.tags) return null;

  return (
    <Box>
      {record.tags.map((tag: string) => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          sx={{
            margin: "2px",
            backgroundColor: "#eee",
            borderRadius: "4px",
          }}
        />
      ))}
    </Box>
  );
};

const DatagridWithPermissions = ({ agentId }: { agentId: string }) => {
  const { hasPermission } = useCustomPermissions();

  return (
    <Datagrid
      bulkActionButtons={
        hasPermission('customer_data', 'delete') ? (
          <BulkDeleteWithConfirmButton
            confirmTitle="Are you sure?"
            confirmContent="This will permanently delete the selected variables."
            mutationMode="pessimistic"
          />
        ) : false
      }
      sx={{
        "& table": {
          tableLayout: "fixed",
          width: "100%",
        },
        "& th, & td": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        "& .column-question": {
          maxWidth: 250,
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
        "& .column-answer": {
          maxWidth: 400,
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      }}
    >
      <TextField
        source="key"
        label="Question"
        cellClassName="column-question"
      />
      <TextField
        source="value"
        label="Answer"
        cellClassName="column-answer"
      />
      <FunctionField
        label="Tags"
        render={(record: any) => <TagChips record={record} />}
        cellClassName="column-tags"
      />
      <FunctionField
        label="Expires At"
        render={(record: any) =>
          record?.expires_at
            ? dayjs(record.expires_at).format("MM/DD/YYYY, h:mm:ss A")
            : ""
        }
        cellClassName="column-expires_at"
      />
      {(hasPermission('customer_data', 'edit') || hasPermission('customer_data', 'delete')) && (
        <FunctionField
          label="Actions"
          render={(record: any) => (
            <ActionsFunctionField agentId={agentId} />
          )}
          cellClassName="column-actions"
        />
      )}
    </Datagrid>
  );
};

const TagFilterWrapper = ({
  selectedTags,
  setSelectedTags,
  searchKeyword,
  setSearchKeyword,
  agentId,
}: {
  selectedTags: string[];
  agentId: string;
  setSelectedTags: (val: string[]) => void;
  searchKeyword: string;
  setSearchKeyword: (val: string) => void;
}) => {
  const listContext = useListContext<any>();
  const { data } = listContext;

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    data?.forEach((record: any) =>
      record.tags?.forEach((t: string) => tags.add(t))
    );
    return Array.from(tags);
  }, [data]);

  // Client-side filtering
  const filteredData = useMemo(() => {
    let result = data || [];
    
    // Filter by tags
    if (selectedTags.length > 0) {
      result = result.filter((record: any) =>
        record.tags?.some((t: string) => selectedTags.includes(t))
      );
    }
    
    // Filter by keyword
    if (searchKeyword && searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase().trim();
      result = result.filter((record: any) => {
        const keyMatch = record.key?.toLowerCase().includes(keyword);
        const valueMatch = record.value?.toLowerCase().includes(keyword);
        return keyMatch || valueMatch;
      });
    }
    
    return result;
  }, [data, selectedTags, searchKeyword]);

  return (
    <Card sx={{ margin: 2 }}>
      <CardHeader title="Dynamic Context" />
      <CardContent>
        <Box sx={{ padding: 2 }}>
          {/* Search and Filter Row */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <MuiTextField
                size="small"
                placeholder="Search keyword"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
              />
            </Box>
            
            <Select
              multiple
              size="small"
              displayEmpty
              value={selectedTags}
              onChange={(e) => setSelectedTags(e.target.value as string[])}
              renderValue={(selected) =>
                selected.length === 0
                  ? "Filter by tags"
                  : (selected as string[]).join(", ")
              }
              sx={{ minWidth: 220 }}
            >
              {allTags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  <Checkbox checked={selectedTags.includes(tag)} />
                  <ListItemText primary={tag} />
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Datagrid */}
          <ListContextProvider
            value={{
              ...listContext,
              data: filteredData,
              total: filteredData.length
            }}
          >
            <DatagridWithPermissions agentId={agentId} />
          </ListContextProvider>
        </Box>
      </CardContent>
    </Card>
  );
};

const TagSelector = (props: any) => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useInput(props);

  const [tagData, setTagData] = useState<any[]>([]);

  const parsedValue = Array.isArray(value)
    ? value
    : value
      ? String(value)
          .split(",")
          .filter((v) => v.trim())
      : [];

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await customerDataApi.getAllTags({
          agentId: agentId ?? "",
        });
        setTagData(res?.data as any);
      } catch (err) {
        console.error("Error fetching config settings", err);
      }
    };

    fetchConfig();
  }, [agentId]);

  return (
    <Autocomplete
      multiple
      style={{ width: "100%" }}
      freeSolo
      options={tagData}
      value={parsedValue}
      onChange={(_, newValue) => onChange(newValue)}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          style={{ width: "100%" }}
          label="Tags"
          error={!!error}
          helperText={error?.message}
        />
      )}
    />
  );
};

const permissionOptions = [{ id: "isPrimary", name: "Primary" }];
const formatWithMicroseconds = (date: Date) => {
  const pad = (n: number, len = 2) => String(n).padStart(len, "0");
  const ms = pad(date.getMilliseconds(), 3) + "000";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}.${ms}`;
};

const selectStyle: React.CSSProperties = {
  padding: "8px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  minHeight: "40px",
};
const normalizeKey = (s: any) =>
  String(s ?? "")
    .replace(/[{}]/g, "")
    .trim();

const GroupMetaData = ({ agentId }: any) => {
  const notify = useNotify();
  const { setValue } = useFormContext();
  const watchedAttributes = useWatch({ name: "attributes" });

  const [metaData, setMetaData] = useState<{ key: string; value: string }[]>(
    () => [{ key: "", value: "" }]
  );

  const [fetchedOptions, setFetchedOptions] = useState<string[]>([]);
  const [fetchedMap, setFetchedMap] = useState<Record<string, string>>({});

  const fetchRatingsReport = async () => {
    try {
      const res = await customerDataApi.getAllAttributes({ agentId });
      const data = res?.data;

      let keys: string[] = [];
      let map: Record<string, string> = {};

      if (Array.isArray(data) && data.length > 0) {
        if (typeof data[0] === "string") {
          keys = (data as string[]).map((s) => normalizeKey(s));
        } else if (typeof data[0] === "object") {
          (data as any[]).forEach((d) => {
            const k = normalizeKey(d.key ?? "");
            const v = d.value ?? "";
            if (k) {
              keys.push(k);
              map[k] = String(v ?? "");
            }
          });
        }
      } else if (data && typeof data === "object") {
        Object.entries(data).forEach(([k, v]) => {
          const nk = normalizeKey(k);
          if (nk) {
            keys.push(nk);
            map[nk] = String(v ?? "");
          }
        });
      }

      keys = Array.from(new Set(keys));

      setFetchedOptions(keys);
      setFetchedMap(map);
    } catch (err) {
      console.error("Error fetching config settings", err);
    }
  };

  useEffect(() => {
    if (agentId) fetchRatingsReport();
  }, [agentId]);

  const lastSetByThisRef = useRef(false);

  const toArray = (attrs: any): { key: string; value: string }[] => {
    if (!attrs) return [];
    if (Array.isArray(attrs)) {
      return attrs.map((a: any) => ({
        key: normalizeKey(a.key),
        value: a.value ?? "",
      }));
    }
    if (typeof attrs === "object") {
      return Object.entries(attrs).map(([k, v]) => ({
        key: normalizeKey(k),
        value: String(v ?? ""),
      }));
    }
    return [];
  };

  useEffect(() => {
    const formArr = toArray(watchedAttributes);
    if (formArr.length) {
      setMetaData(
        formArr.map((m) => ({ key: `{${m.key}}`, value: m.value ?? "" }))
      );
    } else {
      setMetaData([{ key: "", value: "" }]);
    }
  }, []);

  useEffect(() => {
    const normalized = metaData
      .map((m) => ({
        key: normalizeKey(m.key),
        value: String(m.value ?? "").trim(),
      }))
      .filter((m) => m.key !== "" && m.value !== "");

    const formArr = toArray(watchedAttributes);

    if (JSON.stringify(normalized) === JSON.stringify(formArr)) return;

    lastSetByThisRef.current = true;

    if (normalized.length === 0) {
      setValue("attributes", undefined, {
        shouldDirty: true,
        shouldValidate: false,
      });
    } else {
      setValue("attributes", normalized, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [metaData]);

  useEffect(() => {
    const formArr = toArray(watchedAttributes);
    const uiArr = metaData.map((m) => ({
      key: normalizeKey(m.key),
      value: m.value ?? "",
    }));

    if (lastSetByThisRef.current) {
      lastSetByThisRef.current = false;
      return;
    }

    if (JSON.stringify(formArr) === JSON.stringify(uiArr)) {
      return;
    }

    setMetaData(
      formArr.length
        ? formArr.map((m) => ({ key: `{${m.key}}`, value: m.value }))
        : [{ key: "", value: "" }]
    );
  }, [watchedAttributes]);

  const handleMetaDataChange = (
    index: number,
    field: "key" | "value",
    newValue: string
  ) => {
    setMetaData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: newValue };
      return updated;
    });
  };

  const handleAddMetaData = () =>
    setMetaData((prev) => [...prev, { key: "", value: "" }]);

  const handleRemoveMetaData = (index: number) =>
    setMetaData((prev) => prev.filter((_, i) => i !== index));

  const optionsForAutocomplete = useMemo(() => {
    const uiKeys = metaData.map((d) => normalizeKey(d.key)).filter(Boolean);
    return Array.from(new Set([...(fetchedOptions ?? []), ...uiKeys]));
  }, [fetchedOptions, metaData]);

  return (
    <Box width={"100%"} marginBottom={"16px"}>
      {metaData.map((param, index) => (
        <Grid container spacing={2} key={index} alignItems="center" mb={1}>
          <Grid item xs={5}>
            <Autocomplete
              freeSolo
              options={optionsForAutocomplete}
              value={normalizeKey(param.key) ?? ""}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === "input") {
                  handleMetaDataChange(index, "key", newInputValue);
                }
                if (reason === "clear") {
                  handleMetaDataChange(index, "key", "");
                  handleMetaDataChange(index, "value", "");
                }
              }}
              onChange={(event, newValue) => {
                const raw = normalizeKey(newValue);
                if (!raw) return;
                const braceKey = `{${raw}}`;
                handleMetaDataChange(index, "key", braceKey);

                if (fetchedMap && fetchedMap[raw] !== undefined) {
                  handleMetaDataChange(index, "value", fetchedMap[raw]);
                } else {
                  const found = metaData.find(
                    (d) => normalizeKey(d.key) === raw
                  );
                  if (found) handleMetaDataChange(index, "value", found.value);
                }
              }}
              renderInput={(params) => (
                <MuiTextField {...params} label="Attribute Key" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={5}>
            <MuiTextField
              label="Attribute Value"
              value={param.value}
              onChange={(e) =>
                handleMetaDataChange(index, "value", e.target.value)
              }
              fullWidth
            />
          </Grid>

          {param.key && (
            <Grid item xs={1}>
              <Tooltip title="Copy key">
                <IconButton
                  onClick={() => {
                    const textToCopy = normalizeKey(param.key);
                    if (textToCopy) {
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(textToCopy)
                          .then(() => notify("Copied to clipboard", { type: "info" }))
                          .catch(() => notify("Failed to copy", { type: "error" }));
                      } else {
                        // Fallback for non-HTTPS or unsupported browsers
                        const textArea = document.createElement("textarea");
                        textArea.value = textToCopy;
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand("copy");
                          notify("Copied to clipboard", { type: "info" });
                        } catch (err) {
                          notify("Failed to copy", { type: "error" });
                        }
                        document.body.removeChild(textArea);
                      }
                    }
                  }}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          )}

          {index !== 0 && (
            <Grid item xs={1}>
              <IconButton
                color="error"
                onClick={() => handleRemoveMetaData(index)}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          )}
        </Grid>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddMetaData}
      >
        Add Attribute
      </Button>
    </Box>
  );
};

export const ExpirationEditor = () => {
  const { setValue } = useFormContext();
  const expiresAt = useWatch({ name: "expires_at" });
  const [date, setDate] = useState<Date | null>(null);
  const [hour, setHour] = useState("12");
  const [minute, setMinute] = useState("00");
  const [ampm, setAmpm] = useState("AM");

  const now = new Date();
  const maxDate = dayjs().add(180, "day").toDate();

  useEffect(() => {
    let parsed: Date | null = null;

    if (expiresAt) {
      const d = new Date(expiresAt);
      if (!isNaN(d.getTime())) {
        parsed = d;
      }
    }

    if (!parsed) {
      parsed = new Date();
    }

    setDate(parsed);

    let h = parsed.getHours();
    const m = parsed.getMinutes();
    setMinute(String(m).padStart(2, "0"));

    if (h >= 12) {
      setAmpm("PM");
      h = h === 12 ? 12 : h - 12;
    } else {
      setAmpm("AM");
      h = h === 0 ? 12 : h;
    }

    setHour(String(h).padStart(2, "0"));
  }, [expiresAt]);

  useEffect(() => {
    if (date) {
      let h = parseInt(hour, 10);
      const m = parseInt(minute, 10);
      if (ampm === "PM" && h < 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;

      const fullDate = new Date(date);
      fullDate.setHours(h);
      fullDate.setMinutes(m);
      fullDate.setSeconds(0);

      const formatted = formatWithMicroseconds(fullDate);
      setValue("expires_at", formatted, { shouldDirty: true });
    }
  }, [date, hour, minute, ampm, setValue]);

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 1 }}>
        Expires At
      </Typography>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <DatePicker
          selected={date}
          onChange={setDate}
          minDate={now}
          maxDate={maxDate}
          placeholderText="Select date"
          dateFormat="MM/dd/yyyy"
          className="MuiInputBase-input MuiInput-input MuiInputBase-inputAdornedEnd"
        />

        <select
          value={hour}
          onChange={(e) => setHour(e.target.value)}
          style={selectStyle}
        >
          {Array.from({ length: 12 }, (_, i) =>
            String(i + 1).padStart(2, "0")
          ).map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>

        <select
          value={minute}
          onChange={(e) => setMinute(e.target.value)}
          style={selectStyle}
        >
          {Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0")).map(
            (m) => (
              <option key={m} value={m}>
                {m}
              </option>
            )
          )}
        </select>

        <select
          value={ampm}
          onChange={(e) => setAmpm(e.target.value)}
          style={selectStyle}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </Box>
    </Box>
  );
};

const CustomToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

export const ExpirationWrapper = () => {
  const record = useRecordContext();
  if (!record?.expires_at) return null;
  return <ExpirationEditor />;
};

export const DynamicContextEdit = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  return (
    <Edit
      {...props}
      title={false}
      component="div"
      transform={(data: any) => ({
        ...transformEdit(data),
        agent_id: agentId,
        tags: Array.isArray(data.tags) ? data.tags : [],
      })}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Updated successfully", { type: "success" });
          redirect(`/customer-data/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ margin: 2 }}>
        <CardHeader title="Dynamic Context Update" />
        <CardContent>
          <SimpleForm sanitizeEmptyValues toolbar={<CustomToolbar />}>
            <TextInput
              source="key"
              label="Question"
              fullWidth
              multiline
              minRows={3}
              validate={requiredTrimmed("Question is required")}
            />

            <TextInput
              source="value"
              label="Answer"
              fullWidth
              multiline
              minRows={4}
              validate={requiredTrimmed("Answer is required")}
            />
            <TagSelector source="tags" />
            <CheckboxGroupInput
              source="permissions"
              label="Permissions"
              choices={permissionOptions}
            />
            <GroupMetaData agentId={agentId} />

            <ExpirationWrapper />
          </SimpleForm>
        </CardContent>
      </Card>
    </Edit>
  );
};

const ExpirationFields = () => {
  const { watch, setValue } = useFormContext();
  const expirationHours = watch("expiration_hours") || 0;
  const expirationDays = watch("expiration_days") || 0;

  const formatForSubmit = (d: dayjs.Dayjs) =>
    `${d.format("YYYY-MM-DDTHH:mm:ss.SSS")}000`;

  const formatForView = (d: dayjs.Dayjs) => d.format("MM/DD/YYYY, h:mm:ss A");

  const [expiresAt, setExpiresAt] = useState<any>();

  useEffect(() => {
    const totalHours = Number(expirationHours) + Number(expirationDays) * 24;

    if (totalHours > 0) {
      const targetDate = dayjs().add(totalHours, "hour");

      setValue("ttl_hours", totalHours);
      setExpiresAt(formatForView(targetDate));
      setValue("expires_at", formatForSubmit(targetDate));
    } else {
      setValue("ttl_hours", 0);
      setExpiresAt("");
      setValue("expires_at", "");
    }
  }, [expirationHours, expirationDays, setValue]);

  return (
    <>
      <TextInput
        source="expiration_hours"
        label="Expiration Hours (max 24)"
        type="number"
        fullWidth
        parse={(v) => Math.min(Number(v || 0), 24)}
      />
      <TextInput
        source="expiration_days"
        label="Expiration Days (max 180)"
        type="number"
        fullWidth
        parse={(v) => Math.min(Number(v || 0), 180)}
      />
      {expiresAt && (
        <MuiTextField
          label="Expires At"
          value={expiresAt}
          fullWidth
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
          margin="normal"
        />
      )}
    </>
  );
};

export const DynamicContextCreate = (props: any) => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  const notify = useNotify();
  const redirect = useRedirect();

  return (
    <Create
      {...props}
      resource="customer-data"
      title={false}
      component="div"
      redirect={`/customer-data/${agentId || ""}`}
      transform={(data: any) => ({
        ...transformEdit(data),
        agent_id: agentId,
        tags: Array.isArray(data.tags) ? data.tags : [],
      })}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Updated successfully", { type: "success" });
          redirect(`/customer-data/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ margin: 2 }}>
        <CardHeader title="Manage Data" />
        <CardContent>
          <SimpleForm
            sanitizeEmptyValues
            toolbar={
              <Toolbar>
                <SaveButton />
              </Toolbar>
            }
          >
            <TextInput
              source="key"
              label="Question"
              fullWidth
              multiline
              minRows={3}
              validate={requiredTrimmed("Question is required")}
            />

            <TextInput
              source="value"
              label="Answer"
              fullWidth
              multiline
              minRows={4}
              validate={requiredTrimmed("Answer is required")}
            />
            <TagSelector source="tags" />
            <CheckboxGroupInput
              source="permissions"
              label="Permissions"
              choices={permissionOptions}
            />
            <GroupMetaData agentId={agentId} />
            <ExpirationFields />
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};